
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { StorageBrowser } from "./StorageBrowser";
import { STORAGE_BUCKETS } from "@/utils/storageUtils";
import type { FileObject } from "@supabase/storage-js";

interface FilePickerModalProps {
  open: boolean;
  onClose: () => void;
  onFileSelect: (bucketName: string, fileName: string) => void;
  allowedBuckets?: string[];
  title?: string;
}

export const FilePickerModal = ({
  open,
  onClose,
  onFileSelect,
  allowedBuckets,
  title = "Select File from Storage"
}: FilePickerModalProps) => {
  const [selectedFile, setSelectedFile] = useState<FileObject | null>(null);
  const [selectedBucket, setSelectedBucket] = useState<string>("");

  // Get available buckets
  const availableBuckets = allowedBuckets || Object.values(STORAGE_BUCKETS);

  // Initialize selected bucket when modal opens
  useEffect(() => {
    if (open && availableBuckets.length > 0 && !selectedBucket) {
      setSelectedBucket(availableBuckets[0]);
    }
  }, [open, availableBuckets, selectedBucket]);

  const handleFileSelect = (file: FileObject) => {
    setSelectedFile(file);
  };

  const handleTabChange = (value: string) => {
    setSelectedBucket(value);
    setSelectedFile(null); // Clear selection when switching buckets
  };

  const handleConfirm = () => {
    if (selectedFile && selectedBucket) {
      onFileSelect(selectedBucket, selectedFile.name);
      onClose();
      setSelectedFile(null);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedFile(null);
    setSelectedBucket("");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <Tabs 
            value={selectedBucket || availableBuckets[0]} 
            onValueChange={handleTabChange}
            className="h-full flex flex-col"
          >
            <TabsList className="grid w-full grid-cols-4 mb-4">
              {availableBuckets.map((bucket) => (
                <TabsTrigger key={bucket} value={bucket} className="capitalize">
                  {bucket.replace('-', ' ')}
                </TabsTrigger>
              ))}
            </TabsList>

            {availableBuckets.map((bucket) => (
              <TabsContent key={bucket} value={bucket} className="flex-1 overflow-hidden">
                <StorageBrowser
                  bucketName={bucket}
                  onFileSelect={handleFileSelect}
                  selectionMode={true}
                />
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <DialogFooter className="flex justify-between items-center">
          <div className="flex-1">
            {selectedFile && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Selected:</span>{" "}
                <Badge variant="outline" className="ml-1">
                  {selectedBucket}/{selectedFile.name}
                </Badge>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={!selectedFile}
            >
              Select File
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
